"use client"

import { clx, Drawer } from "@medusajs/ui"
import LocalizedClientLink from "@/modules/common/components/localized-client-link"
import { usePathname } from "next/navigation"
import { useState, useEffect, useRef } from "react"
import Image from "next/image"
import type { CategoryNavItem as CategoryWithStrapi } from "../category-nav"
import {
  compareCategoriesByRank,
  getCategoryRoutePath,
  isCategoryActiveInPathname,
} from "@/lib/util/category-helpers"

interface CategoryDropdownProps {
  category: CategoryWithStrapi
  allCategories: CategoryWithStrapi[]
  strapiData?: CategoryWithStrapi["strapiData"]
  variant?: "default" | "under-row"
}

const STRAPI_URL = process.env.NEXT_PUBLIC_STRAPI_URL || process.env.STRAPI_URL || "https://strapi.krek.si"

// Helper to extract image URL from Strapi format
const getImageUrl = (media: any): string | null => {
  if (!media) return null

  // Handle direct array of images (new Strapi structure)
  if (Array.isArray(media)) {
    const firstImage = media[0]
    if (firstImage?.url) {
      const imageUrl = firstImage.formats?.large?.url || 
                      firstImage.formats?.medium?.url || 
                      firstImage.formats?.small?.url || 
                      firstImage.url
      return imageUrl.startsWith('http') ? imageUrl : `${STRAPI_URL}${imageUrl}`
    }
  }

  // Handle single object format
  if (media?.url) {
    const imageUrl = media.formats?.large?.url || 
                     media.formats?.medium?.url || 
                     media.formats?.small?.url || 
                     media.url
    return imageUrl.startsWith('http') ? imageUrl : `${STRAPI_URL}${imageUrl}`
  }

  return null
}

export default function CategoryDropdown({
  category,
  allCategories,
  strapiData,
  variant = "default",
}: CategoryDropdownProps) {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [navbarHeight, setNavbarHeight] = useState(128) // Fallback when nav not measured yet
  const pathname = usePathname()
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Get subcategories for this category
  const getSubCategories = (categoryId: string) => {
    return allCategories
      .filter((cat) => cat.parent_category_id === categoryId)
      .sort(compareCategoriesByRank)
  }

  const subCategories = getSubCategories(category.id)
  const hasSubCategories = subCategories.length > 0

  const medusaHref = (cat: CategoryWithStrapi) =>
    `/categories/${getCategoryRoutePath(cat, allCategories)}`

  const isNavActive = (cat: CategoryWithStrapi) =>
    isCategoryActiveInPathname(pathname, cat, allCategories)

  const isActive = isNavActive(category)

  // Get Strapi data
  const heroImageUrl = strapiData?.heroImage ? getImageUrl(strapiData.heroImage) : null
  const description = strapiData?.description || category.description || ""
  const title = strapiData?.title || strapiData?.name || category.name

  const handleMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    if (hasSubCategories || heroImageUrl || description) {
      setIsDrawerOpen(true)
    }
  }

  const handleMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsDrawerOpen(false)
    }, 200)
  }

  const handleDrawerMouseEnter = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }

  const handleDrawerMouseLeave = () => {
    timeoutRef.current = setTimeout(() => {
      setIsDrawerOpen(false)
    }, 200)
  }

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    setIsDrawerOpen(false)
  }, [pathname])

  // Calculate navbar height dynamically
  useEffect(() => {
    const calculateNavbarHeight = () => {
      const floatingNav = document.querySelector(
        "[data-floating-nav-root]"
      ) as HTMLElement | null
      if (floatingNav) {
        setNavbarHeight(floatingNav.getBoundingClientRect().bottom)
      }
    }

    // Calculate on mount and when drawer opens
    calculateNavbarHeight()
    
    // Recalculate on window resize
    window.addEventListener('resize', calculateNavbarHeight)
    
    return () => {
      window.removeEventListener('resize', calculateNavbarHeight)
    }
  }, [isDrawerOpen])

  // Handle overlay hover to keep drawer open
  useEffect(() => {
    if (!isDrawerOpen) return

    const handleOverlayMouseEnter = () => {
      // Cancel any pending close
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }

    const handleOverlayMouseLeave = (e: MouseEvent) => {
      const relatedTarget = e.relatedTarget as HTMLElement
      
      // Check if moving to drawer content
      if (relatedTarget?.closest('[role="dialog"]')) {
        return // Don't close if moving to drawer
      }
      
      // Check if moving to another overlay
      if (relatedTarget?.closest('.bg-ui-bg-overlay')) {
        return // Don't close if moving to overlay
      }
      
      // Only close if leaving overlay area entirely
      setIsDrawerOpen(false)
    }

    // Function to find and attach to the open overlay
    const attachToOpenOverlay = () => {
      const openOverlay = document.querySelector('[data-state="open"].bg-ui-bg-overlay') as HTMLElement
      
      if (openOverlay) {
        openOverlay.addEventListener('mouseenter', handleOverlayMouseEnter)
        openOverlay.addEventListener('mouseleave', handleOverlayMouseLeave)
        
        return () => {
          openOverlay.removeEventListener('mouseenter', handleOverlayMouseEnter)
          openOverlay.removeEventListener('mouseleave', handleOverlayMouseLeave)
        }
      }
      
      return undefined
    }

    // Try to attach immediately
    let cleanup = attachToOpenOverlay()

    // If not found, try again after a short delay
    const timeoutId = setTimeout(() => {
      cleanup = attachToOpenOverlay() || cleanup
    }, 100)

    // Watch for overlay changes
    const observer = new MutationObserver(() => {
      if (cleanup) {
        cleanup()
      }
      cleanup = attachToOpenOverlay() || undefined
    })

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state']
    })

    return () => {
      clearTimeout(timeoutId)
      if (cleanup) {
        cleanup()
      }
      observer.disconnect()
    }
  }, [isDrawerOpen])

  const isUnderRow = variant === "under-row"

  return (
    <>
      <div
        className="relative"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <LocalizedClientLink
          href={medusaHref(category)}
          className={clx(
            "flex items-center gap-1 transition-all duration-200",
            isUnderRow
              ? clx(
                  "px-1 py-0.5 text-sm text-cream",
                  isActive
                    ? "font-bold text-white"
                    : "font-normal text-cream/90 hover:text-white"
                )
              : clx(
                  "px-4 py-2 rounded-lg",
                  isActive
                    ? "text-gold bg-navy/10"
                    : "text-navy hover:text-gold hover:bg-navy/5"
                )
          )}
        >
          <span className={isUnderRow ? undefined : "font-medium"}>
            {category.name}
          </span>
        </LocalizedClientLink>
      </div>

      <Drawer open={isDrawerOpen} onOpenChange={setIsDrawerOpen} modal={false}>
        <Drawer.Content
          className="!w-full !max-w-full z-[55] rounded-none m-0 p-0 !fixed !left-0 !right-0 !bottom-auto max-h-[calc(80vh-6rem)] flex flex-col"
          overlayProps={{ className: "z-[55]" }}
          onMouseEnter={handleDrawerMouseEnter}
          onMouseLeave={handleDrawerMouseLeave}
          style={{ top: `${navbarHeight}px` }}
        >
          <Drawer.Title className="sr-only">{title}</Drawer.Title>

          <div className="flex max-h-[calc(80vh-6rem)] overflow-y-auto bg-white shadow-2xl">
            {/* Left Column - Navigation Menu */}
            <div className="flex-shrink-0 w-64 bg-gray-50 p-6 border-r border-gray-200">
              {/* Title */}
              <h2 className="text-xl font-bold text-navy mb-6">{title}</h2>

              {/* Subcategories Navigation */}
              {hasSubCategories && (
                <nav className="space-y-1">
                  {subCategories.map((subCategory) => {
                    const isSubActive = isNavActive(subCategory)

                    return (
                      <div key={subCategory.id} className="mb-2">
                        <LocalizedClientLink
                          href={medusaHref(subCategory)}
                          className={clx(
                            "block px-3 py-2 text-sm transition-colors rounded",
                            isSubActive
                              ? "text-navy bg-white font-medium shadow-sm"
                              : "text-gray-700 hover:text-navy hover:bg-white/50"
                          )}
                          onClick={() => setIsDrawerOpen(false)}
                        >
                          {subCategory.name}
                        </LocalizedClientLink>
                      </div>
                    )
                  })}
                </nav>
              )}

              {/* View Category Link */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <LocalizedClientLink
                  href={medusaHref(category)}
                  className="block text-sm text-navy font-medium hover:text-gold transition-colors"
                  onClick={() => setIsDrawerOpen(false)}
                >
                  Oglej si vse izdelke →
                </LocalizedClientLink>
              </div>
            </div>

            {/* Right Column - Mini shop window: subcategory tiles or hero + description */}
            <div className="flex-1 p-6 min-h-0 flex flex-col">
              {hasSubCategories ? (
                /* Subcategory tiles with images (IKEA/Williams Sonoma style) */
                <div className="flex-1 grid grid-cols-2 lg:grid-cols-4 gap-4 content-start">
                  {subCategories.slice(0, 8).map((subCategory) => {
                    const subStrapi = subCategory.strapiData
                    const subImgUrl =
                      subStrapi
                        ? getImageUrl(subStrapi.heroImage) ||
                          getImageUrl(subStrapi.bannerImage) ||
                          getImageUrl(subStrapi?.image)
                        : null
                    const subName = subStrapi?.name || subCategory.name

                    return (
                      <LocalizedClientLink
                        key={subCategory.id}
                        href={medusaHref(subCategory)}
                        className="group block"
                        onClick={() => setIsDrawerOpen(false)}
                      >
                        <div className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 shadow-sm hover:shadow-md transition-all duration-200 border border-gray-100 group-hover:border-sky-light">
                          {subImgUrl ? (
                            <Image
                              src={subImgUrl}
                              alt={subName}
                              fill
                              sizes="(max-width: 1024px) 50vw, 25vw"
                              className="object-cover transition-transform duration-200 group-hover:scale-105"
                            />
                          ) : (
                            <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200">
                              <span className="text-gray-500 text-sm font-medium text-center px-2">
                                {subName}
                              </span>
                            </div>
                          )}
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                          <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent">
                            <span className="text-white font-medium text-sm drop-shadow-sm">
                              {subName}
                            </span>
                          </div>
                        </div>
                      </LocalizedClientLink>
                    )
                  })}
                </div>
              ) : heroImageUrl ? (
                /* No subcategories: hero image + description */
                <div className="flex gap-6 items-start">
                  <div className="relative w-64 h-64 flex-shrink-0 rounded-lg overflow-hidden shadow-md">
                    <Image
                      src={heroImageUrl}
                      alt={title}
                      fill
                      className="object-cover"
                      sizes="256px"
                    />
                  </div>
                  {description && (
                    <div className="flex-1 text-gray-700 prose prose-sm max-w-none">
                      {description.includes("<") ? (
                        <div dangerouslySetInnerHTML={{ __html: description }} />
                      ) : (
                        <p className="whitespace-pre-wrap">{description}</p>
                      )}
                    </div>
                  )}
                </div>
              ) : description ? (
                <div className="text-gray-700 prose prose-sm max-w-none">
                  {description.includes("<") ? (
                    <div dangerouslySetInnerHTML={{ __html: description }} />
                  ) : (
                    <p className="whitespace-pre-wrap">{description}</p>
                  )}
                </div>
              ) : null}
            </div>
          </div>
        </Drawer.Content>
      </Drawer>
    </>
  )
}

